/* eslint-disable */
import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Card,
  CardHeader,
  CardBody,
  CardIcon,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faExclamationTriangle, 
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { client } from "../../../../services/axiosClient.js";
import { ThreeDots } from "react-loader-spinner";
import AuthContext from "contexts/AuthProvider.js";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import RiskTableV2 from "./RiskTableV2.js";

// ================== Constantes para Status das Ações ==================
const ACTION_STATUS = {
  ON_HOLD: "On Hold",
  IN_PROGRESS: "In Progress", 
  DONE: "Done"
};

// ================== Funções de Estilo ==================

// Função para calcular o status geral de um Risk Level
const getRiskLevelGeneralStatus = (riskLevel, risksList) => {
  if (!risksList || !Array.isArray(risksList)) return "Green";
  
  const risksInLevel = risksList.filter(risk => risk.risk_level === riskLevel);
  if (risksInLevel.length === 0) return "Green";
  
  // Se algum risco está vermelho, o status geral é vermelho
  if (risksInLevel.some(risk => risk.status === "Red")) return "Red";
  // Se algum risco está amarelo, o status geral é amarelo
  if (risksInLevel.some(risk => risk.status === "Yellow")) return "Yellow";
  // Se todos estão verdes, o status geral é verde
  return "Green";
};

// Função para obter a cor do indicador do Risk Level
const getRiskLevelIndicatorColor = (riskLevel) => {
  switch (riskLevel) {
    case "High":
      return "#0f0f23"; // Mesma cor da matriz High (escuro)
    case "Medium":
      return "#2d3748"; // Mesma cor da matriz Medium (médio)
    case "Low":
      return "#718096"; // Mesma cor da matriz Low (claro)
    default:
      return "#9E9E9E"; // Gray
  }
};

// Função para obter o estilo do círculo de status
const getStatusCircleStyle = (status) => {
  let background, borderColor;
  switch (status) {
    case "Red":
      background = "#FFEBEE"; // light red
      borderColor = "#E57373";
      break;
    case "Yellow":
      background = "#FFF9C4"; // light yellow
      borderColor = "#FFB74D";
      break;
    case "Green":
      background = "#C8E6C9"; // light green
      borderColor = "#81C784";
      break;
    default:
      background = "#E0E0E0";
      borderColor = "#9E9E9E";
  }
  return {
    background,
    border: `2px solid ${borderColor}`,
    borderRadius: "50%",
    width: "35px",
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  };
};

// Função para obter a cor do progresso
function getProgressColor(percentage) {
  if (percentage >= 100) {
    return "#4CAF50"; // Green
  } else if (percentage >= 50) {
    return "#FFC107"; // Yellow
  } else {
    return "#F44336"; // Red
  }
}

// Função para calcular o progresso de um plano específico
function calculatePlanProgress(risk, planType) {
  if (planType === "mitigation" && !risk.hasMitigationPlan) return 100;
  if (planType === "contingency" && !risk.hasContingencyPlan) return 100;

  let totalActions = 0;
  let doneActions = 0;
  if (!risk.rootCauses || !Array.isArray(risk.rootCauses)) return 100;

  risk.rootCauses.forEach((rc) => {
    if (rc.actions && Array.isArray(rc.actions)) {
      const filtered = rc.actions.filter((act) => act.type === planType);
      totalActions += filtered.length;
      filtered.forEach((act) => {
        if (act.status === ACTION_STATUS.DONE) doneActions += 1;
      });
    }
  });

  if (totalActions === 0) return 100;
  return Math.round((doneActions / totalActions) * 100);
}

// Função para calcular estatísticas de ações
function calculateActionStats(risk, planType) {
  if (planType === "mitigation" && !risk.hasMitigationPlan) return { total: 0, completed: 0, expectedToday: 0 };
  if (planType === "contingency" && !risk.hasContingencyPlan) return { total: 0, completed: 0, expectedToday: 0 };

  let totalActions = 0;
  let completedActions = 0;
  let expectedTodayActions = 0;
  const today = new Date().toISOString().split('T')[0];

  if (risk.rootCauses && Array.isArray(risk.rootCauses)) {
    risk.rootCauses.forEach((rc) => {
      if (rc.actions && Array.isArray(rc.actions)) {
        const filtered = rc.actions.filter((act) => act.type === planType);
        totalActions += filtered.length;
        
        filtered.forEach((act) => {
          if (act.status === ACTION_STATUS.DONE) {
            completedActions += 1;
          } else if (act.dueDate && act.dueDate <= today) {
            expectedTodayActions += 1;
          }
        });
      }
    });
  }

  return { total: totalActions, completed: completedActions, expectedToday: expectedTodayActions };
}



// ================== Componente Principal ==================

const RiskSummaryV2 = ({ 
  clientColor, 
  isLoading: externalIsLoading, 
  isModalOpen, 
  onCloseModal,
  cardHeight = "20vh", // Altura padrão do card
  isCompact = false, // Modo compacto para cards menores
  onOpenModalWithFilter,
  initialFilter,
}) => {
  // Detectar automaticamente se deve estar em modo compacto baseado na altura
  const isAutoCompact = cardHeight && (
    cardHeight.includes("20vh") || 
    cardHeight.includes("25vh") || 
    cardHeight.includes("30vh") ||
    (typeof cardHeight === "string" && cardHeight.includes("vh") && parseInt(cardHeight) <= 30)
  );
  
  // Usar o modo compacto automático se não foi especificado explicitamente
  const shouldBeCompact = isCompact !== false ? isCompact : isAutoCompact;
  const { t } = useTranslation();
  const { userDetails } = useContext(AuthContext);
  const [riskData, setRiskData] = useState(null);
  // Loading interno sempre inicia true - loading externo é só para sincronizar exibição
  const [isLoading, setIsLoading] = useState(true);
  
  // Cache para evitar múltiplas chamadas desnecessárias
  const [lastFetchedFilter, setLastFetchedFilter] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  // Usar loading externo se fornecido, caso contrário usar interno
  const shouldShowLoading = externalIsLoading !== undefined ? externalIsLoading : isLoading;

  // Função para abrir o modal com filtro específico
  const handleRiskLevelClick = useCallback((riskLevel) => {
    if (onOpenModalWithFilter) {
      onOpenModalWithFilter({ risk_level: riskLevel });
    }
  }, [onOpenModalWithFilter]);

  // Estilos
  const useStyles = makeStyles((theme) => ({
    root: {
      padding: 0,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center", // Centralizar verticalmente
      alignItems: "center", // Centralizar horizontalmente
    },
    cardHeader: {
      padding: "10px 20px",
      minHeight: "60px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    cardIconTitle: {
      fontSize: shouldBeCompact ? "0.9rem" : "1rem",
      fontWeight: "600",
      margin: 0,
      color: "#333",
    },
    cardBody: {
      flex: 1,
      overflow: "auto", // Mesmo padrão do key message
      display: "flex",
      flexDirection: "column",
      padding: 0, // Sem padding para aproveitar todo o espaço
    },
    // Estilos para o resumo compacto - Layout horizontal otimizado com margens
    summaryContainer: {
      display: "flex",
      flexDirection: "row", // Mudança para horizontal
      gap: shouldBeCompact ? "6px" : "8px", // Reduzido para economizar espaço
      minHeight: "100%", // Altura mínima para centralizar
      alignItems: "center", // Centralizar verticalmente
      justifyContent: "center", // Centralizar horizontalmente também
      padding: shouldBeCompact ? "8px" : "12px", // Padding uniforme
      maxWidth: "100%", // Garantir que não ultrapasse a largura
    },
    // Total de Riscos - Lado esquerdo, mais compacto
    totalRisksSection: {
      textAlign: "center",
      padding: shouldBeCompact ? "6px 8px" : "8px 12px", // Padding reduzido
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: `2px solid ${clientColor}20`,
      marginRight: shouldBeCompact ? "6px" : "8px", // Margin reduzido
      minWidth: shouldBeCompact ? "65px" : "85px", // Largura reduzida
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center", // Centralizar horizontalmente
      minHeight: "fit-content", // Altura baseada no conteúdo
      maxHeight: "100%", // Máximo de altura
    },
    totalRisksLabel: {
      fontSize: shouldBeCompact ? "0.55rem" : "0.65rem", // Fonte aumentada para melhor legibilidade
      fontWeight: "600",
      color: "#666",
      textTransform: "uppercase",
      marginBottom: "1px", // Margin reduzido
      lineHeight: 1,
    },
    totalRisksValue: {
      fontSize: shouldBeCompact ? "1.2rem" : "1.4rem", // Fonte aumentada para destaque
      fontWeight: "700",
      color: clientColor,
      margin: 0,
      lineHeight: 1,
    },
    // Risk Levels - Layout horizontal expandido com novo design
    riskLevelsContainer: {
      display: "flex",
      flexDirection: "row",
      gap: shouldBeCompact ? "4px" : "6px", // Gap reduzido para economizar espaço
      flex: 1, // Ocupar o espaço restante
      minHeight: "fit-content", // Altura baseada no conteúdo
      maxHeight: "100%", // Máximo de altura
      alignItems: "center", // Centralizar verticalmente
      justifyContent: "center", // Centralizar horizontalmente
    },
    riskLevelCard: {
      flex: 1,
      display: "flex",
      flexDirection: "column", // Volta para vertical para acomodar a barra de indicador
      alignItems: "center",
      padding: shouldBeCompact ? "6px 4px" : "8px 6px", // Padding reduzido
      cursor: "pointer", // Indicar que é clicável
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "2px solid",
      cursor: "pointer",
      transition: "all 0.2s ease",
      minHeight: "fit-content", // Altura baseada no conteúdo
      maxHeight: "100%", // Máximo de altura
      justifyContent: "center", // Centralizar verticalmente
      minWidth: 0, // Permitir que encolha
    },
    // Barra indicadora no topo (mantida como antes)
    riskLevelIndicator: {
      width: "100%",
      height: "3px",
      borderRadius: "2px",
      marginBottom: shouldBeCompact ? "4px" : "6px", // Reduzido para economizar espaço
    },
    // Novo layout: Status Circle + Conteúdo horizontal
    riskLevelContent: {
      display: "flex",
      flexDirection: "row", // Layout horizontal para o conteúdo
      alignItems: "center",
      gap: shouldBeCompact ? "4px" : "6px", // Gap reduzido
      justifyContent: "center",
      flex: 1,
      minWidth: 0,
      padding: shouldBeCompact ? "2px 0" : "4px 0", // Padding vertical reduzido
      textAlign: "center", // Centralizar texto
    },
    // Status Circle
    riskLevelStatusSection: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      textAlign: "center", // Centralizar texto
    },
    riskLevelCount: {
      fontSize: shouldBeCompact ? "1.1rem" : "1.2rem", // Fonte aumentada para destaque
      fontWeight: "700",
      margin: 0,
      lineHeight: 1,
      flexShrink: 0,
    },
    riskLevelLabel: {
      fontSize: shouldBeCompact ? "0.65rem" : "0.75rem", // Fonte aumentada para melhor legibilidade
      fontWeight: "600",
      color: "#666",
      textTransform: "uppercase",
      margin: 0,
      lineHeight: 1,
      textAlign: "center",
      flexShrink: 0,
    },
    riskLevelPercentage: {
      fontSize: shouldBeCompact ? "0.6rem" : "0.7rem", // Fonte aumentada para melhor legibilidade
      color: "#999",
      margin: 0,
      lineHeight: 1,
      textAlign: "center",
      flexShrink: 0,
    },
    // Status circle otimizado para o novo layout
    statusCircle: {
      width: shouldBeCompact ? "24px" : "28px",
      height: shouldBeCompact ? "24px" : "28px",
    },
    // Estilos para o modal
    modalTitle: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 24px",
      borderBottom: `1px solid ${clientColor}20`,
      backgroundColor: "#f8f9fa",
    },
    modalContent: {
      padding: 0,
      minHeight: "600px",
      overflow: "hidden",
    },
    closeButton: {
      color: "#999",
      "&:hover": {
        color: clientColor,
      },
    },
    // Estilos para loading e mensagens
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      minHeight: "120px",
    },
    noDataMessage: {
      textAlign: "center",
      color: "#999",
      fontSize: isCompact ? "0.65rem" : "0.75rem",
      fontStyle: "italic",
      padding: "20px",
    },
  }));

  const classes = useStyles();

  // Função para fechar o modal
  const handleCloseModal = () => {
    onCloseModal();
  };

  // Buscar dados dos riscos - Otimizado com cache
  const fetchRiskData = useCallback(async () => {
    // Verificar se já está buscando dados para evitar chamadas duplicadas
    if (isFetching) {
      console.log("[RiskSummaryV2] Já está buscando dados, ignorando nova chamada");
      return;
    }
    
    // Verificar se os dados já foram buscados com o mesmo filtro
    const currentFilterKey = JSON.stringify(initialFilter || {});
    if (lastFetchedFilter === currentFilterKey && riskData) {
      console.log("[RiskSummaryV2] Dados já existem para este filtro, reutilizando cache");
      setIsLoading(false);
      return;
    }
    
    console.log("[RiskSummaryV2] Iniciando busca de dados para:", initialFilter);
    setIsLoading(true);
    setIsFetching(true);
    
    try {
      // Preparar payload base
      const basePayload = {
        action: "list",
      };
      
      // Adicionar filtro de iniciativa se fornecido
      if (initialFilter?.initiative_id) {
        basePayload.initiative_id = initialFilter.initiative_id;
      }

      // Buscar dados do resumo (para o card principal)
      const summaryResponse = await client.post("/api/risk-summary-data/", basePayload);

      // Buscar dados completos dos riscos (para a tabela)
      const risksPayload = {
        action: "list_risks",
      };
      
      // Adicionar filtro de iniciativa se fornecido
      if (initialFilter?.initiative_id) {
        risksPayload.initiative_id = initialFilter.initiative_id;
      }
      
      const risksResponse = await client.post("/api/risk-management/", risksPayload);

      if (summaryResponse.data?.success && risksResponse.data?.success) {
        const risks = risksResponse.data.risks || [];
        
        // Calcular contadores baseado no campo risk_level de cada risco
        const risk_counts = risks.reduce((acc, risk) => {
          const level = risk.risk_level;
          if (level) {
            acc[level] = (acc[level] || 0) + 1;
          }
          return acc;
        }, {});
        
        const total_risks = risks.length;
        
        setRiskData({
          risk_counts,
          total_risks,
          completed_actions: summaryResponse.data.data.completed_actions || 0,
          expected_today_actions: summaryResponse.data.data.expected_today_actions || 0,
          risks,
          branchOptions: risksResponse.data.branch_options || [],
          userOptions: risksResponse.data.user_options || [],
        });
        
        // Atualizar o cache do filtro
        setLastFetchedFilter(currentFilterKey);
        console.log("[RiskSummaryV2] Dados carregados e cache atualizado para:", initialFilter);
      } else {
        console.warn("APIs retornaram erro, usando dados mock");
        // Fallback para dados mock
        const mockRisks = [
          {
            id: 1,
            title: "Risco de Prazo",
            risk_level: "High",
            status: "Red",
            owner: "João Silva",
            branch: "TI",
            probability: "High",
            impact: "High",
            hasMitigationPlan: true,
            hasContingencyPlan: false,
            rootCauses: [
              {
                id: "RC1",
                cause: "Falta de recursos",
                actions: [
                  { id: "AC1", type: "mitigation", status: ACTION_STATUS.DONE, title: "Contratar desenvolvedor", dueDate: "2025-01-15" },
                  { id: "AC2", type: "mitigation", status: ACTION_STATUS.IN_PROGRESS, title: "Revisar cronograma", dueDate: "2025-01-20" }
                ]
              }
            ]
          },
          {
            id: 2,
            title: "Risco de Qualidade",
            risk_level: "Medium",
            status: "Yellow",
            owner: "Maria Santos",
            branch: "Qualidade",
            probability: "Medium",
            impact: "High",
            hasMitigationPlan: true,
            hasContingencyPlan: true,
            rootCauses: [
              {
                id: "RC2",
                cause: "Processos inadequados",
                actions: [
                  { id: "AC3", type: "mitigation", status: ACTION_STATUS.DONE, title: "Implementar QA", dueDate: "2025-01-10" },
                  { id: "AC4", type: "contingency", status: ACTION_STATUS.ON_HOLD, title: "Plano de contingência", dueDate: "2025-01-25" }
                ]
              }
            ]
          }
        ];
        
        const risk_counts = mockRisks.reduce((acc, risk) => {
          const level = risk.risk_level;
          if (level) {
            acc[level] = (acc[level] || 0) + 1;
          }
          return acc;
        }, {});
        
        const mockData = {
          risk_counts,
          total_risks: mockRisks.length,
          completed_actions: 15,
          expected_today_actions: 8,
          risks: mockRisks,
          branchOptions: [
            { id: 1, name: "TI" },
            { id: 2, name: "Qualidade" },
            { id: 3, name: "Operações" }
          ],
          userOptions: [
            { id: 1, name: "João Silva" },
            { id: 2, name: "Maria Santos" },
            { id: 3, name: "Pedro Costa" }
          ]
        };
        setRiskData(mockData);
      }
    } catch (err) {
      console.error("Erro ao buscar dados dos riscos:", err);
      // Fallback para dados mock em caso de erro
      const mockRisks = [
        {
          id: 1,
          title: "Risco de Prazo",
          risk_level: "High",
          status: "Red",
          owner: "João Silva",
          branch: "TI",
          probability: "High",
          impact: "High",
          hasMitigationPlan: true,
          hasContingencyPlan: false,
          rootCauses: [
            {
              id: "RC1",
              cause: "Falta de recursos",
              actions: [
                { id: "AC1", type: "mitigation", status: ACTION_STATUS.DONE, title: "Contratar desenvolvedor", dueDate: "2025-01-15" },
                { id: "AC2", type: "mitigation", status: ACTION_STATUS.IN_PROGRESS, title: "Revisar cronograma", dueDate: "2025-01-20" }
              ]
            }
          ]
        },
        {
          id: 2,
          title: "Risco de Qualidade",
          risk_level: "Medium",
          status: "Yellow",
          owner: "Maria Santos",
          branch: "Qualidade",
          probability: "Medium",
          impact: "High",
          hasMitigationPlan: true,
          hasContingencyPlan: true,
          rootCauses: [
            {
              id: "RC2",
              cause: "Processos inadequados",
              actions: [
                { id: "AC3", type: "mitigation", status: ACTION_STATUS.DONE, title: "Implementar QA", dueDate: "2025-01-10" },
                { id: "AC4", type: "contingency", status: ACTION_STATUS.ON_HOLD, title: "Plano de contingência", dueDate: "2025-01-25" }
              ]
            }
          ]
        }
      ];
      
      const risk_counts = mockRisks.reduce((acc, risk) => {
        const level = risk.risk_level;
        if (level) {
          acc[level] = (acc[level] || 0) + 1;
        }
        return acc;
      }, {});
      
      const mockData = {
        risk_counts,
        total_risks: mockRisks.length,
        completed_actions: 15,
        expected_today_actions: 8,
        risks: mockRisks,
        branchOptions: [
          { id: 1, name: "TI" },
          { id: 2, name: "Qualidade" },
          { id: 3, name: "Operações" }
        ],
        userOptions: [
          { id: 1, name: "João Silva" },
          { id: 2, name: "Maria Santos" },
          { id: 3, name: "Pedro Costa" }
        ]
      };
      setRiskData(mockData);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [initialFilter, isFetching, lastFetchedFilter, riskData]);

  useEffect(() => {
    // Só buscar dados se não estiver carregando externamente e se o filtro mudou
    if (!externalIsLoading) {
      const currentFilterKey = JSON.stringify(initialFilter || {});
      if (lastFetchedFilter !== currentFilterKey || !riskData) {
        fetchRiskData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter, externalIsLoading]); // fetchRiskData intencionalmente omitido para evitar loops

  // Renderizar loading
  if (shouldShowLoading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingContainer}>
          <ThreeDots color={clientColor} height={80} width={80} ariaLabel="loading" />
        </div>
      </div>
    );
  }

  // Se não há dados, mostrar mensagem
  if (!riskData) {
    return (
      <div className={classes.root}>
        <div className={classes.noDataMessage}>
          {t("riskmanagement.no_risks_registered")}
        </div>
      </div>
    );
  }

  // Configuração de cores para cada nível de risco
  const riskLevelConfig = {
    High: { color: "#0f0f23", label: t("riskmanagement.risk_levels.high") },
    Medium: { color: "#2d3748", label: t("riskmanagement.risk_levels.medium") },
    Low: { color: "#718096", label: t("riskmanagement.risk_levels.low") },
  };

  // Verificar se há dados para exibir
  const hasData = riskData.total_risks > 0;

  if (!hasData) {
    return (
      <div className={classes.root}>
        <div className={classes.noDataMessage}>
          {t("riskmanagement.no_risks_registered")}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* CardBody com PerfectScrollbar - Estrutura semelhante ao key message */}
      <div className={classes.cardBody}>
        <PerfectScrollbar>
      {/* Resumo Compacto */}
      <div className={classes.summaryContainer}>
        {/* Total de Riscos - Centralizado no topo */}
        <div className={classes.totalRisksSection}>
          <Typography className={classes.totalRisksLabel}>
            {t("riskmanagement.totalRisksLabel")}
          </Typography>
          <Typography className={classes.totalRisksValue}>
            {riskData.total_risks}
          </Typography>
        </div>

        {/* Risk Levels - Layout horizontal */}
        <div className={classes.riskLevelsContainer}>
          {/* High Risk Level */}
          <div 
            className={classes.riskLevelCard}
            style={{ 
              border: "2px solid #e9ecef",
              borderTop: `6px solid ${getRiskLevelIndicatorColor("High")}`,
              boxShadow: "none"
            }}
                onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRiskLevelClick("High");
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
                {/* Conteúdo horizontal: Status Circle + Número + Label + Porcentagem */}
            <div className={classes.riskLevelContent}>
                  {/* Status Circle */}
                  <div className={classes.riskLevelStatusSection}>
                    <div 
                      style={{
                        ...getStatusCircleStyle(getRiskLevelGeneralStatus("High", riskData.risks)),
                        width: shouldBeCompact ? "24px" : "28px",
                        height: shouldBeCompact ? "24px" : "28px",
                      }}
                    />
                  </div>
                  
                  {/* Número de Riscos */}
              <Typography 
                className={classes.riskLevelCount}
                style={{ color: getRiskLevelIndicatorColor("High") }}
              >
                {riskData.risk_counts["High"] || 0}
              </Typography>
                  
                  {/* Nível do Risco com estrutura "NÍVEL" + "ALTO" */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1px"
                  }}>
                                                  <Typography 
                                style={{ 
                                  fontSize: shouldBeCompact ? "0.4rem" : "0.5rem", 
                                  fontWeight: "800", 
                                  color: getRiskLevelIndicatorColor("High"),
                                  textTransform: "uppercase",
                                  margin: 0,
                                  lineHeight: 1
                                }}
                              >
                                {t("riskmanagement.risk_levels.level")}
                              </Typography>
                              <Typography 
                                style={{ 
                                  fontSize: shouldBeCompact ? "0.5rem" : "0.6rem", 
                                  fontWeight: "700", 
                                  color: getRiskLevelIndicatorColor("High"),
                                  textTransform: "uppercase",
                                  margin: 0,
                                  lineHeight: 1
                                }}
                              >
                                {t("riskmanagement.risk_levels.high")}
                              </Typography>
                  </div>
                  
                  {/* Porcentagem */}
              <Typography className={classes.riskLevelPercentage}>
                ({riskData.total_risks > 0 ? Math.round(((riskData.risk_counts["High"] || 0) / riskData.total_risks) * 100) : 0}%)
              </Typography>
            </div>
            
          </div>

          {/* Medium Risk Level */}
          <div 
            className={classes.riskLevelCard}
            style={{ 
              border: "2px solid #e9ecef",
              borderTop: `6px solid ${getRiskLevelIndicatorColor("Medium")}`,
              boxShadow: "none"
            }}
                onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRiskLevelClick("Medium");
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
                {/* Conteúdo horizontal: Status Circle + Número + Label + Porcentagem */}
            <div className={classes.riskLevelContent}>
                  {/* Status Circle */}
                  <div className={classes.riskLevelStatusSection}>
                    <div 
                      style={{
                        ...getStatusCircleStyle(getRiskLevelGeneralStatus("Medium", riskData.risks)),
                        width: shouldBeCompact ? "24px" : "28px",
                        height: shouldBeCompact ? "24px" : "28px",
                      }}
                    />
                  </div>
                  
                  {/* Número de Riscos */}
              <Typography 
                className={classes.riskLevelCount}
                style={{ color: getRiskLevelIndicatorColor("Medium") }}
              >
                {riskData.risk_counts["Medium"] || 0}
              </Typography>
                  
                  {/* Nível do Risco com estrutura "NÍVEL" + "MÉDIO" */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1px"
                  }}>
                                                  <Typography 
                                style={{ 
                                  fontSize: shouldBeCompact ? "0.4rem" : "0.5rem", 
                                  fontWeight: "800", 
                                  color: getRiskLevelIndicatorColor("Medium"),
                                  textTransform: "uppercase",
                                  margin: 0,
                                  lineHeight: 1
                                }}
                              >
                                {t("riskmanagement.risk_levels.level")}
                              </Typography>
                              <Typography 
                                style={{ 
                                  fontSize: shouldBeCompact ? "0.5rem" : "0.6rem", 
                                  fontWeight: "700", 
                                  color: getRiskLevelIndicatorColor("Medium"),
                                  textTransform: "uppercase",
                                  margin: 0,
                                  lineHeight: 1
                                }}
                              >
                                {t("riskmanagement.risk_levels.medium")}
                              </Typography>
                  </div>
                  
                  {/* Porcentagem */}
              <Typography className={classes.riskLevelPercentage}>
                ({riskData.total_risks > 0 ? Math.round(((riskData.risk_counts["Medium"] || 0) / riskData.total_risks) * 100) : 0}%)
              </Typography>
            </div>
            
          </div>

          {/* Low Risk Level */}
          <div 
            className={classes.riskLevelCard}
            style={{ 
              border: "2px solid #e9ecef",
              borderTop: `6px solid ${getRiskLevelIndicatorColor("Low")}`,
              boxShadow: "none"
            }}
                onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRiskLevelClick("Low");
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
                {/* Conteúdo horizontal: Status Circle + Número + Label + Porcentagem */}
            <div className={classes.riskLevelContent}>
                  {/* Status Circle */}
                  <div className={classes.riskLevelStatusSection}>
                    <div 
                      style={{
                        ...getStatusCircleStyle(getRiskLevelGeneralStatus("Low", riskData.risks)),
                        width: shouldBeCompact ? "24px" : "28px",
                        height: shouldBeCompact ? "24px" : "28px",
                      }}
                    />
                  </div>
                  
                  {/* Número de Riscos */}
              <Typography 
                className={classes.riskLevelCount}
                style={{ color: getRiskLevelIndicatorColor("Low") }}
              >
                {riskData.risk_counts["Low"] || 0}
              </Typography>
                  
                  {/* Nível do Risco com estrutura "NÍVEL" + "BAIXO" */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1px"
                  }}>
                                                  <Typography 
                                style={{ 
                                  fontSize: shouldBeCompact ? "0.4rem" : "0.5rem", 
                                  fontWeight: "800", 
                                  color: getRiskLevelIndicatorColor("Low"),
                                  textTransform: "uppercase",
                                  margin: 0,
                                  lineHeight: 1
                                }}
                              >
                                {t("riskmanagement.risk_levels.level")}
                              </Typography>
                              <Typography 
                                style={{ 
                                  fontSize: shouldBeCompact ? "0.5rem" : "0.6rem", 
                                  fontWeight: "700", 
                                  color: getRiskLevelIndicatorColor("Low"),
                                  textTransform: "uppercase",
                                  margin: 0,
                                  lineHeight: 1
                                }}
                              >
                                {t("riskmanagement.risk_levels.low")}
                              </Typography>
                  </div>
                  
                  {/* Porcentagem */}
              <Typography className={classes.riskLevelPercentage}>
                ({riskData.total_risks > 0 ? Math.round(((riskData.risk_counts["Low"] || 0) / riskData.total_risks) * 100) : 0}%)
              </Typography>
            </div>
            
          </div>
        </div>
          </div>
        </PerfectScrollbar>
      </div>

      {/* Modal de Detalhes com wrapper para forçar z-index */}
      <div 
        style={{ 
          position: 'fixed', 
          zIndex: 10100, 
          pointerEvents: isModalOpen ? 'auto' : 'none' // Só ativar eventos quando modal estiver aberto
        }}
      >
        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="xl"
          fullWidth
          className="risk-dialog"
          style={{ 
            zIndex: 10100,
            position: 'relative'
          }}
          PaperProps={{
            className: "risk-dialog-paper",
            style: {
              borderRadius: "12px",
              maxHeight: "90vh",
              width: "95vw",
              zIndex: 10100,
              position: 'relative'
            },
          }}
          BackdropProps={{
            className: "risk-dialog-backdrop",
            style: { 
              backgroundColor: "rgba(0, 0, 0, 0.6)", // Backdrop mais forte para visibilidade
              zIndex: 10099,
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            },
          }}
          // Garantir que seja renderizado diretamente no body
          container={() => document.body}
          disablePortal={false}
          // Garantir que eventos de clique funcionem
          disableBackdropClick={false}
          disableEscapeKeyDown={false}
          // Força que o modal seja posicionado acima de tudo
          hideBackdrop={false}
          keepMounted={false}
        >
        <div className={classes.modalTitle}>
          <Typography variant="h6" style={{ color: clientColor, fontWeight: "600" }}>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              style={{ marginRight: "8px", color: clientColor }}
            />
            {t("riskmanagement.risk_management")}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            className={classes.closeButton}
            size="small"
          >
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </div>

        <DialogContent className={classes.modalContent} style={{ padding: 0, overflow: "hidden" }}>
          {riskData ? (
             <RiskTableV2
              risks={riskData.risks || []}
              branchOptions={riskData.branchOptions || []}
              userOptions={riskData.userOptions || []}
              clientColor={clientColor}
              t={t}
               initialFilter={initialFilter}
            />
          ) : (
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              flexDirection: "column",
              gap: "16px"
            }}>
              <ThreeDots color={clientColor} height={80} width={80} ariaLabel="loading" />
              <Typography variant="body2" style={{ color: "#666", fontSize: "0.8rem" }}>
                {t("riskmanagement.loading_risk_data")}
              </Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default RiskSummaryV2;
